/**
 * @fileoverview Local Database Manager — the single process that owns the
 *               entire local database lifecycle for all FireSquirrel web apps.
 *               Nothing else creates, clears, or writes metadata to the database.
 * @module local-db/services/local-database-manager
 *
 * Responsibilities:
 * - Open Dexie, read `_db_meta`, compare against expected metadata.
 * - Determine recovery action (backfill-metadata, incremental migration,
 *   wipe-and-rebuild, update-app-first, or none).
 * - Execute recovery: backfill-metadata (write _db_meta in place, no data
 *   loss, for pre-manager databases that already contain data), incremental
 *   migration (Dexie upgrades auto-run), wipe-and-rebuild (clear IndexedDB,
 *   recreate, write _db_meta, clear checkpoints), or update-app-first (force
 *   service worker update + reload).
 * - Report status to the sync widget and React components.
 * - Enforce write locking — `canWrite` is true only during 'ready' and
 *   'syncing-pending-writes' states.
 * - Provide offline fallback using compiled-in constants.
 *
 * Dependencies:
 * - dexie
 * - ../types/dbMeta (types only)
 * - ../replication/replicationFactory (clearAllSyncCheckpoints — re-exported)
 *
 * Architecture:
 * - Authority vs Executor split: the API is the source of truth for expected
 *   metadata (MongoDB `local_db_metadata`); the frontend executes recovery
 *   (only the browser can touch IndexedDB). When online, the API's decision
 *   is authoritative. When offline, the frontend uses compiled-in constants.
 * - The manager is a factory — each app creates its own instance with its
 *   own config (dbId, openDb function, constants, collection map).
 * - `_db_meta` is NOT in any sync config map. It is written only by this
 *   manager during creation or after recovery.
 *
 * Notes:
 * - Phase 1 implements the local `determineRecoveryAction()` logic. Phase 2
 *   adds the API decision endpoint as the primary path when online.
 * - Phase 3 wires the service worker update trigger for `update-app-first`.
 * - Phase 5 adds the mobile-specific pre-sync logic (mobileDatabaseManager).
 * - Per-migration write locks (`holdWrites`) are Phase 8 (deferred). The
 *   MIGRATION_REGISTRY is read but all migrations currently default to
 *   `holdWrites: false`.
 *
 * @logging
 * Events:    local_db_recovery_decision (info), local_db_wipe_and_rebuild (info),
 *            local_db_open_failed (error).
 * Level:     info / warn / error.
 * Correlation: dbId, environment, schemaVersion — callers should set these as
 *            global telemetry properties before passing the client.
 *
 * @errorHandling
 * Strategy:  Catch Dexie/IndexedDB errors, set status='error', and re-throw.
 * User Impact: Sync widget shows 'error'; the app provider can retry on the next
 *            auth change.
 * Logging:   Exceptions are tracked through the shared telemetry client; no
 *            sensitive values (connection strings, tokens, etc.) are logged.
 */

import Dexie from 'dexie';
import { WebTelemetry } from '@firesquirrel/web-telemetry';
import type {
  DbMeta,
  ExpectedDbMeta,
  RecoveryAction,
  RecoveryDecision,
  DbStatus,
  DbId,
  MigrationMeta,
} from '../types/dbMeta';
import {
  readDbMeta,
  writeDbMeta,
  buildDbMeta,
  dbHasData,
  clearPullCheckpoints,
  clearSyncNonce,
  migrationRangeHasHoldWrites,
} from './dbMetaHelpers';
import { registerDbCloseHandler } from './localDbErrorRecovery';

// ---------------------------------------------------------------------------
// Public config interface
// ---------------------------------------------------------------------------

/**
 * @contract LocalDatabaseManagerConfig
 *
 * Purpose:
 * - Configuration for creating a localDatabaseManager instance for a specific app.
 *
 * Fields:
 * - `dbId`: Which app this manager owns ('system-web' | 'tenant-web' | 'mobile-pwa').
 * - `dbName`: The IndexedDB database name (e.g. 'firesquirrel-system').
 * - `openDb`: Function that opens the Dexie singleton and returns the wrapped DB.
 * - `closeDb`: Function that closes the Dexie singleton and resets the cached promise.
 * - `getRawDexie`: Function that returns the raw Dexie instance (or null if unopened).
 * - `expectedSchemaVersion`: Compiled-in EXPECTED_DB_SCHEMA_VERSION constant.
 * - `minMigratableVersion`: Compiled-in MIN_MIGRATABLE_VERSION constant.
 * - `migrationRegistry`: Compiled-in MIGRATION_REGISTRY (per-version metadata).
 * - `collectionMap`: Sync collection map (Dexie key → Mongo collection) for clearing checkpoints.
 * - `checkpointPrefix`: Optional checkpoint namespace prefix (e.g. 'system', 'tenant').
 * - `isMobile`: Whether this manager is for a mobile/field context (enables pre-sync logic).
 * - `onUpdateAppFirst`: Optional callback for triggering service worker update + reload
 *   (Phase 3 wires this; Phase 1 just sets status='updating-app').
 * - `syncPendingWrites`: Optional callback for flushing dirty writes to the server
 *   before a wipe-and-rebuild recovery destroys them. Receives the CURRENTLY
 *   OPEN (pre-wipe) db instance — the callback MUST drive its own push
 *   mechanism against that instance (e.g. by calling `setupReplication` and
 *   waiting for it to drain) rather than relying on the host app's normal
 *   `startSync`/React-driven replication, which has not started yet at this
 *   point in the recovery flow (see the `syncing-pending-writes` state
 *   diagram in `executeWipeAndRebuild`'s doc comment for why). Returns a
 *   `SyncPendingWritesResult` describing whether the flush actually confirmed
 *   everything was pushed, and how many docs (if known) were still
 *   unconfirmed. A `void`/undefined return is treated as fully flushed, for
 *   backward compatibility with callbacks that don't report an outcome.
 * - `onSyncPendingWritesIncomplete`: Optional callback invoked ONLY when
 *   `syncPendingWrites` reports (or its failure implies) that real,
 *   non-zero unsynced data may still exist. Lets the host app show a
 *   blocking "some changes may not be saved" prompt and returns the user's
 *   choice: 'retry' (call `syncPendingWrites` again, up to
 *   `maxSyncPendingWritesRetries` times) or 'continue' (proceed with the
 *   wipe anyway). If omitted, the wipe proceeds automatically (previous
 *   default behavior) with NO further warning. If retries ARE exhausted,
 *   this callback is still invoked one final time with `isFinalAttempt:
 *   true` so the user is informed before the wipe proceeds unconditionally
 *   (the returned decision is ignored on that final call — there is no more
 *   retrying to do) — this is NOT a silent wipe once the callback is
 *   configured at all. The manager races each call's promise against
 *   `syncPendingWritesDecisionTimeoutMs` and defaults to 'continue' if it
 *   doesn't resolve in time, so a user who never responds (e.g. backgrounds
 *   the app) cannot leave recovery stuck forever.
 *
 * Rules:
 * - `openDb` must be the existing getXxxDb singleton function.
 * - `closeDb` must be the existing destroyXxxDb function (closes + nulls promise).
 * - After `closeDb`, calling `openDb` again must create a fresh instance.
 *
 * Sync:
 * - Not synced — configuration is compiled into the frontend bundle.
 */
export interface LocalDatabaseManagerConfig<Db = unknown> {
  dbId: DbId;
  dbName: string;
  openDb: () => Promise<Db>;
  closeDb: () => Promise<void>;
  getRawDexie: () => Dexie | null;
  expectedSchemaVersion: number;
  minMigratableVersion: number;
  migrationRegistry: Record<number, MigrationMeta>;
  collectionMap: Record<string, string>;
  checkpointPrefix?: string;
  isMobile?: boolean;
  onUpdateAppFirst?: () => Promise<void>;
  syncPendingWrites?: (db: Db) => Promise<SyncPendingWritesResult | void>;
  onSyncPendingWritesIncomplete?: (info: {
    pendingCount: number;
    attempt: number;
    /**
     * True when `maxSyncPendingWritesRetries` has already been used up —
     * this is the LAST time the host gets a chance to inform the user
     * before the wipe proceeds unconditionally. The returned decision is
     * ignored when this is true (there is no more retrying left to do);
     * hosts should render this state without a "Retry" affordance (or with
     * one that's clearly informational only) to avoid implying a retry
     * click here will do anything.
     */
    isFinalAttempt: boolean;
  }) => Promise<'retry' | 'continue'>;
  /**
   * Maximum number of times `syncPendingWrites` is retried (beyond the
   * initial attempt) when `onSyncPendingWritesIncomplete` returns 'retry'.
   * Defaults to 1 (one retry, two attempts total).
   */
  maxSyncPendingWritesRetries?: number;
  /**
   * Maximum time to wait for `onSyncPendingWritesIncomplete` to resolve
   * before defaulting to 'continue'. Defaults to 120000ms (2 minutes) so a
   * backgrounded/ignored app cannot leave recovery blocked indefinitely.
   */
  syncPendingWritesDecisionTimeoutMs?: number;
  /**
   * Optional shared telemetry client. When provided, recovery actions and errors
   * are tracked through the WebTelemetry client rather than plain console output.
   */
  telemetry?: WebTelemetry;
  /**
   * Maximum time to wait for `Dexie.delete()` during wipe-and-rebuild before
   * treating it as blocked (another tab/connection has the database open —
   * `indexedDB.deleteDatabase()` has no native timeout and hangs indefinitely
   * in that case). Defaults to 8000ms. Configurable primarily for tests.
   */
  deleteTimeoutMs?: number;
}

/**
 * @contract SyncPendingWritesResult
 * Purpose: Outcome of one `syncPendingWrites` attempt, reported back to the
 *          manager so it can decide whether the user needs to be warned
 *          before an active wipe-and-rebuild proceeds.
 * Fields:
 * - `flushed`: True if the push queue was CONFIRMED fully drained before
 *   this resolved (not merely "no error was thrown").
 * - `pendingCount`: Number of docs still unconfirmed-pushed when this
 *   resolved. Use `-1` when the true count is unknown (e.g. the flush
 *   couldn't even start — no auth token, no tenant context) rather than
 *   claiming a false "0".
 * Rules: `flushed: true` implies `pendingCount` should be `0`.
 * Sync: Not synced — in-memory recovery signal only.
 */
export interface SyncPendingWritesResult {
  flushed: boolean;
  pendingCount: number;
}

// ---------------------------------------------------------------------------
// Manager instance interface
// ---------------------------------------------------------------------------

/**
 * @contract LocalDatabaseManager
 *
 * Purpose:
 * - The single process that owns the local database lifecycle for one app.
 *
 * Fields:
 * - `open`: Opens the DB, validates _db_meta, executes recovery, returns the DB.
 * - `getStatus`: Returns the current lifecycle status.
 * - `subscribeStatus`: Subscribes to status changes (returns unsubscribe function).
 * - `getCanWrite`: Returns whether writes are currently allowed.
 * - `getDb`: Returns the current DB instance (null unless status is 'ready' or 'syncing-pending-writes').
 * - `close`: Closes the DB and resets the manager.
 *
 * Rules:
 * - `getDb()` returns non-null only when status is 'syncing-pending-writes' or 'ready'.
 * - `getCanWrite()` returns true only when status is 'syncing-pending-writes' or 'ready'.
 *
 * Sync:
 * - Not synced — local lifecycle management.
 */
export interface LocalDatabaseManager<Db = unknown> {
  open(expectedMetadata: ExpectedDbMeta | null, isOnline: boolean): Promise<Db>;
  getStatus(): DbStatus;
  subscribeStatus(listener: (status: DbStatus) => void): () => void;
  getCanWrite(): boolean;
  getDb(): Db | null;
  close(): Promise<void>;
}

// ---------------------------------------------------------------------------
// compareSemver — numeric, dot-separated version comparison
// ---------------------------------------------------------------------------

/**
 * @reusable compareSemver
 *
 * Purpose:
 * - Compare two `x.y.z` style version strings numerically by component.
 *
 * Returns:
 * - Negative if `a < b`, positive if `a > b`, 0 if equal.
 *
 * Rules:
 * - Empty or non-numeric segments are treated as 0.
 * - Segments are split on `.`; longer versions with trailing zeros are equal.
 * - Empty or missing inputs are coerced to '0' before comparison.
 *
 * Logging:
 * - Not logged; pure utility.
 */
function compareSemver(a: string, b: string): number {
  const parse = (v: string) => v.split('.').map((s) => {
    const n = parseInt(s, 10);
    return Number.isNaN(n) ? 0 : n;
  });
  const av = parse(a || '0');
  const bv = parse(b || '0');
  const len = Math.max(av.length, bv.length);
  for (let i = 0; i < len; i++) {
    const an = av[i] ?? 0;
    const bn = bv[i] ?? 0;
    if (an !== bn) return an - bn;
  }
  return 0;
}

/**
 * Thrown when the `update-app-first` recovery path was triggered but the
 * hard navigation did not execute. The hook checks for this error type so it
 * does not immediately retry `open()` and flash between splash and error.
 *
 * `retryable` distinguishes two cases:
 * - `false` (default): the navigation was attempted but did not commit
 *   (browser blocked it, watchdog fired). Retrying `open()` on the same deps
 *   just flashes between splash and error — the hook latches `initDone`.
 * - `true`: no callback was configured or the app is offline. The caller
 *   SHOULD retry when online or when auth/metadata changes. The hook does NOT
 *   latch `initDone` so the effect can re-run.
 */
export class UpdateAppFirstError extends Error {
  readonly retryable: boolean;
  constructor(message: string, retryable = false) {
    super(message);
    this.name = 'UpdateAppFirstError';
    this.retryable = retryable;
  }
}

// ---------------------------------------------------------------------------
// determineRecoveryAction — pure decision logic (exported for testing + API reuse)
// ---------------------------------------------------------------------------

/**
 * @process determineRecoveryAction
 *
 * Steps:
 * 1. If localMeta is null (no _db_meta record), return 'backfill-metadata' if
 *    data exists (pre-manager database — adopt it in place, no data loss), or
 *    'none' if the DB is empty (fresh creation).
 * 2. If dbId mismatch → 'wipe-and-rebuild' (database belongs to a different app).
 * 3. If environment mismatch → 'wipe-and-rebuild' (cross-environment data bleed).
 * 4. If apiBaseUrl mismatch → 'wipe-and-rebuild' (synced against a different API).
 * 5. If local schemaVersion > expected → 'wipe-and-rebuild' (code doesn't know this schema).
 * 6. If local schemaVersion < expected:
 *    a. If code's compiled EXPECTED_DB_SCHEMA_VERSION < expected.schemaVersion → 'update-app-first'
 *       (running code is too old to handle the migration).
 *    b. If local schemaVersion < MIN_MIGRATABLE_VERSION → 'wipe-and-rebuild' (too old to migrate).
 *    c. Otherwise → 'incremental-migration' (Dexie upgrade functions handle it).
 * 7. If schema matches and `compareSemver(local.appVersion, expected.appVersion) < 0`
 *    → 'none' (stamp `_db_meta.appVersion` in `open()`). Reloading here loops:
 *    `update-app-first` does not write `_db_meta` before navigation, so the
 *    freshly loaded page still has the old appVersion and re-triggers.
 *    Schema-too-old is the only remaining `update-app-first` path (step 6a).
 * 8. Otherwise → 'none' (match).
 *
 * Inputs:
 * - localMeta: The _db_meta record from IndexedDB (or null if not present).
 * - expected: The server-authoritative expected metadata (or compiled-in fallback).
 * - compiledSchemaVersion: The EXPECTED_DB_SCHEMA_VERSION constant from the DB file.
 * - minMigratableVersion: The MIN_MIGRATABLE_VERSION constant from the DB file.
 * - dbHasData: Whether the DB contains any data (used to distinguish fresh DB from wiped DB).
 *
 * Outputs:
 * - RecoveryDecision: { action, reason }
 *
 * Failure Handling:
 * - Pure function — never throws. All edge cases return a defined action.
 *
 * Idempotency:
 * - REQUIRED — same inputs always produce the same decision.
 */
export function determineRecoveryAction(
  localMeta: DbMeta | null,
  expected: ExpectedDbMeta,
  compiledSchemaVersion: number,
  minMigratableVersion: number,
  dbHasData: boolean,
): RecoveryDecision {
  // No _db_meta record — either a fresh DB or a pre-manager DB that needs initialization
  if (!localMeta) {
    if (dbHasData) {
      // The database predates this manager (e.g. an existing production user
      // upgrading to the build that introduced `_db_meta`). Do NOT wipe —
      // adopt the existing data in place by writing `_db_meta` from the
      // expected metadata. Wiping here would destroy real user data (including
      // any unsynced offline writes) for a condition that has nothing to do
      // with data corruption or environment mismatch.
      return {
        action: 'backfill-metadata',
        reason: 'No _db_meta record but DB has data — pre-manager database, backfilling _db_meta in place without wiping',
      };
    }
    return {
      action: 'none',
      reason: 'No _db_meta record and DB is empty — fresh database, manager will write _db_meta on open',
    };
  }

  // dbId mismatch — database belongs to a different app
  if (localMeta.dbId !== expected.dbId) {
    return {
      action: 'wipe-and-rebuild',
      reason: `dbId mismatch: local=${localMeta.dbId}, expected=${expected.dbId}`,
    };
  }

  // Environment mismatch — cross-environment data bleed
  if (localMeta.environment !== expected.environment) {
    return {
      action: 'wipe-and-rebuild',
      reason: `Environment mismatch: local=${localMeta.environment}, expected=${expected.environment}`,
    };
  }

  // apiBaseUrl mismatch — synced against a different API
  if (localMeta.apiBaseUrl !== expected.apiBaseUrl) {
    return {
      action: 'wipe-and-rebuild',
      reason: `apiBaseUrl mismatch: local=${localMeta.apiBaseUrl}, expected=${expected.apiBaseUrl}`,
    };
  }

  // Schema is newer than code expects — code doesn't know this schema
  if (localMeta.schemaVersion > expected.schemaVersion) {
    return {
      action: 'wipe-and-rebuild',
      reason: `Local schemaVersion ${localMeta.schemaVersion} is newer than expected ${expected.schemaVersion} — code cannot handle this schema`,
    };
  }

  // Schema is older than expected — need migration or update
  if (localMeta.schemaVersion < expected.schemaVersion) {
    // Check if the running code is too old to handle the migration
    if (compiledSchemaVersion < expected.schemaVersion) {
      return {
        action: 'update-app-first',
        reason: `Compiled schemaVersion ${compiledSchemaVersion} is less than API expected ${expected.schemaVersion} — code is too old to migrate, update app first`,
      };
    }

    // Check if the local DB is too old to migrate
    if (localMeta.schemaVersion < minMigratableVersion) {
      return {
        action: 'wipe-and-rebuild',
        reason: `Local schemaVersion ${localMeta.schemaVersion} is below MIN_MIGRATABLE_VERSION ${minMigratableVersion} — no migration scripts available`,
      };
    }

    return {
      action: 'incremental-migration',
      reason: `Schema upgrade from ${localMeta.schemaVersion} to ${expected.schemaVersion} — within migratable range`,
    };
  }

  // Schema matches. An older local appVersion is not a reason to reload —
  // the running bundle already compiled this schema. Stamping `_db_meta` in
  // open() is enough. Reloading here is what caused the `__fs_update` loop:
  // update-app-first never writes IndexedDB, so the next load still sees
  // the stale appVersion and tries to reload again.
  if (compareSemver(localMeta.appVersion, expected.appVersion) < 0) {
    return {
      action: 'none',
      reason: `Local appVersion ${localMeta.appVersion} is older than expected ${expected.appVersion} — stamp _db_meta in place (schema already matches)`,
    };
  }

  return {
    action: 'none',
    reason: 'Local _db_meta matches expected metadata',
  };
}

// ---------------------------------------------------------------------------
// Manager factory
// ---------------------------------------------------------------------------

/**
 * @process createLocalDatabaseManager
 *
 * Steps:
 * 1. Create a manager instance with the given config.
 * 2. The manager maintains internal state: status, db, listeners.
 * 3. `open()` is called by the app provider on startup with expected metadata.
 * 4. The manager opens Dexie, reads _db_meta, determines recovery action,
 *    executes it, and returns the DB.
 * 5. Status changes are broadcast to all subscribers.
 *
 * Inputs:
 * - config: LocalDatabaseManagerConfig for the specific app.
 *
 * Outputs:
 * - LocalDatabaseManager instance.
 *
 * Failure Handling:
 * - If recovery fails (IndexedDB error), status='error' and the error is thrown.
 * - If openDb fails, status='error' and the error is thrown.
 *
 * Idempotency:
 * - NOT REQUIRED — open() can be called multiple times (e.g. after recovery).
 *   Subsequent calls return the already-open DB if status is 'ready'.
 *
 * @logging
 * Events:   local_db_manager_created (info), local_db_recovery_decision (info),
 *           local_db_wipe_and_rebuild (info), local_db_incremental_migration (info),
 *           local_db_update_app_first (warn), local_db_open_failed (error).
 * Level:    info / warn / error.
 * Correlation: dbId, environment, schemaVersion, action, reason.
 *
 * @errorHandling
 * Strategy: Wrap all recovery executors in try/catch, set status='error', and
 *           track the exception before re-throwing.
 * User Impact: UI displays 'error' status; sync widget can prompt for retry.
 * Logging:  trackException for all thrown errors; event properties exclude
 *           sensitive data.
 */
export function createLocalDatabaseManager<Db>(
  config: LocalDatabaseManagerConfig<Db>,
): LocalDatabaseManager<Db> {
  // Register close callback for error screen database deletion
  registerDbCloseHandler(async () => {
    try {
      await config.closeDb();
    } catch {
      // Best-effort
    }
  });

  // Internal state
  let status: DbStatus = 'initializing';
  let db: Db | null = null;
  const listeners = new Set<(status: DbStatus) => void>();

  function setStatus(newStatus: DbStatus): void {
    if (status === newStatus) return;
    status = newStatus;
    for (const listener of listeners) {
      try { listener(newStatus); } catch { /* ignore listener errors */ }
    }
  }

  function getDb(): Db | null {
    // db is non-null only when status is 'syncing-pending-writes' or 'ready'
    if (status === 'syncing-pending-writes' || status === 'ready') {
      return db;
    }
    return null;
  }

  function getCanWrite(): boolean {
    return status === 'syncing-pending-writes' || status === 'ready';
  }

  function subscribeStatus(listener: (status: DbStatus) => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }

  // -------------------------------------------------------------------------
  // Recovery executors
  // -------------------------------------------------------------------------

  /**
   * Execute wipe-and-rebuild recovery.
   * 1. (Mobile only) Sync pending writes before wiping.
   * 2. Close the DB.
   * 3. Delete the IndexedDB database.
   * 4. Clear localStorage pull checkpoints + nonce.
   * 5. Reopen the DB (fresh, at current schema version).
   * 6. Write _db_meta from expected metadata.
   *
   * IMPORTANT — why `config.syncPendingWrites` receives the pre-wipe db and
   * MUST NOT rely on the host app's normal React-driven `startSync`:
   * `setStatus('syncing-pending-writes')` below notifies subscribers
   * SYNCHRONOUSLY (plain function calls, not a React event), which schedules
   * a React state update but does NOT synchronously flush effects. The very
   * next line — `await config.syncPendingWrites(preWipeDb)` — begins
   * executing the callback's body SYNCHRONOUSLY (up to its first `await`)
   * in the SAME turn of the JS event loop, before any microtask/React commit
   * can run. So at the moment this callback starts, the host app's
   * `startSync`/`setupReplication()` has not been called yet for this
   * session — any code that only *reacts* to the status change (e.g.
   * waking an already-running push loop, or checking an already-populated
   * `pendingPushCount`) is a guaranteed no-op. The callback must actively
   * start its OWN push mechanism against `preWipeDb` and wait for it.
   */
  async function executeWipeAndRebuild(expected: ExpectedDbMeta): Promise<Db> {
    // Capture the pre-wipe db reference before it is nulled below.
    const preWipeDb = db as Db;

    // Mobile: sync pending writes before wiping. If the flush cannot confirm
    // everything was pushed AND there is real unconfirmed data (not just "no
    // error was thrown"), give the host app a bounded number of retries and
    // a chance to warn the user via onSyncPendingWritesIncomplete before
    // silently proceeding with the wipe. See SyncPendingWritesResult and
    // onSyncPendingWritesIncomplete docs on LocalDatabaseManagerConfig.
    if (config.isMobile && config.syncPendingWrites) {
      setStatus('syncing-pending-writes');
      const maxRetries = config.maxSyncPendingWritesRetries ?? 1;
      const decisionTimeoutMs = config.syncPendingWritesDecisionTimeoutMs ?? 120_000;
      let attempt = 0;
      for (;;) {
        attempt++;
        let result: SyncPendingWritesResult = { flushed: true, pendingCount: 0 };
        try {
          const outcome = await config.syncPendingWrites(preWipeDb);
          if (outcome) result = outcome;
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          config.telemetry?.trackEvent('local_db_sync_pending_writes_failed', {
            dbId: config.dbId,
            dbName: config.dbName,
            error: message,
            attempt: String(attempt),
          });
          // Unknown outcome from a thrown error — treat conservatively as
          // unconfirmed data at risk rather than assuming nothing was
          // pending, so the user still gets a chance to see this.
          result = { flushed: false, pendingCount: -1 };
        }

        const hasUnconfirmedData = !result.flushed && result.pendingCount !== 0;
        if (!hasUnconfirmedData) break;

        config.telemetry?.trackEvent('local_db_sync_pending_writes_incomplete', {
          dbId: config.dbId,
          dbName: config.dbName,
          pendingCount: String(result.pendingCount),
          attempt: String(attempt),
        });

        if (!config.onSyncPendingWritesIncomplete) {
          config.telemetry?.trackEvent('local_db_sync_pending_writes_proceeding_unconfirmed', {
            dbId: config.dbId,
            dbName: config.dbName,
            pendingCount: String(result.pendingCount),
          });
          break;
        }

        // Once retries are exhausted, still show the warning ONE more time
        // (with isFinalAttempt: true) rather than silently proceeding —
        // a user who clicked Retry and it failed again deserves to be told
        // that this is the last chance, not have the wipe happen with zero
        // further indication. The returned decision is ignored below (there
        // is no more retrying to do); this call is informational only.
        const isFinalAttempt = attempt > maxRetries;

        // Race the host's decision UI against a bounded timeout so a user
        // who never responds (backgrounds the app, walks away) cannot leave
        // recovery stuck in 'syncing-pending-writes' forever. The timer is
        // explicitly cleared once the race settles either way, so a fast
        // response doesn't leave a dangling timer sitting around for the
        // remainder of decisionTimeoutMs.
        let decisionTimer: ReturnType<typeof setTimeout> | undefined;
        const decision = await Promise.race([
          config.onSyncPendingWritesIncomplete({ pendingCount: result.pendingCount, attempt, isFinalAttempt }),
          new Promise<'continue'>((resolve) => {
            decisionTimer = setTimeout(() => resolve('continue'), decisionTimeoutMs);
          }),
        ]);
        clearTimeout(decisionTimer);

        if (isFinalAttempt) {
          if (decision === 'retry') {
            // The user asked to retry again, but retries are exhausted —
            // note this in telemetry so it's visible that the wipe proceeded
            // over an explicit (ignored) retry request, not just a timeout.
            config.telemetry?.trackEvent('local_db_sync_pending_writes_retry_exhausted', {
              dbId: config.dbId,
              dbName: config.dbName,
              pendingCount: String(result.pendingCount),
            });
          }
          break;
        }

        if (decision !== 'retry') break;
        // else: loop back and call config.syncPendingWrites again.
      }
    }

    setStatus('recovering');
    db = null;

    // Close the current DB
    try {
      await config.closeDb();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      config.telemetry?.trackEvent('local_db_close_before_wipe_failed', {
        dbId: config.dbId,
        dbName: config.dbName,
        error: message,
      });
    }

    // Delete the IndexedDB database. `indexedDB.deleteDatabase()` (which
    // Dexie.delete() wraps) has NO timeout and hangs forever — not rejects,
    // hangs — if any other tab/connection still has this database open (a
    // well-known IndexedDB "blocked" pitfall). Without a race against a
    // timeout here, a single stale tab/DevTools connection permanently
    // strands the manager in 'recovering', sync never starts, and there is
    // no error surfaced anywhere. Race against a bounded timeout so recovery
    // always terminates and the failure is visible.
    const deleteTimeoutMs = config.deleteTimeoutMs ?? 8_000;
    const deleteOutcome = await Promise.race([
      Dexie.delete(config.dbName).then(() => 'deleted' as const),
      new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), deleteTimeoutMs)),
    ]).catch((err) => {
      const message = err instanceof Error ? err.message : String(err);
      config.telemetry?.trackEvent('local_db_indexeddb_delete_failed', {
        dbId: config.dbId,
        dbName: config.dbName,
        error: message,
      });
      return 'deleted' as const; // proceed — the delete request itself failed, not blocked
    });

    if (deleteOutcome === 'timeout') {
      config.telemetry?.trackEvent('local_db_indexeddb_delete_blocked', {
        dbId: config.dbId,
        dbName: config.dbName,
        timeoutMs: String(deleteTimeoutMs),
      });
      console.error(
        `[local-db] IndexedDB delete for "${config.dbName}" is blocked by another open connection ` +
        `(another browser tab, or a DevTools Application panel inspecting this database). ` +
        `Close all other tabs/windows for this origin and reload. Proceeding to reopen on top of ` +
        `the existing database — recovery will retry on next load if data does not match.`,
      );
    } else {
      config.telemetry?.trackEvent('local_db_indexeddb_deleted', {
        dbId: config.dbId,
        dbName: config.dbName,
      });
    }

    // Clear localStorage pull checkpoints + nonce
    clearPullCheckpoints(config.collectionMap, config.checkpointPrefix);
    clearSyncNonce(config.dbName, config.checkpointPrefix);

    // Reopen the DB (fresh, at current schema version)
    const reopenedDb = await config.openDb();
    db = reopenedDb;

    // Write _db_meta
    const rawDexie = config.getRawDexie();
    if (rawDexie) {
      const now = new Date().toISOString();
      await writeDbMeta(rawDexie, buildDbMeta(expected, now));
      config.telemetry?.trackEvent('local_db_wipe_and_rebuild_complete', {
        dbId: expected.dbId,
        environment: expected.environment,
        schemaVersion: String(expected.schemaVersion),
      });
    }

    return reopenedDb;
  }

  /**
   * Execute incremental migration recovery.
   * Dexie upgrade functions have already run during open().
   * Write new _db_meta from expected metadata.
   */
  async function executeIncrementalMigration(
    expected: ExpectedDbMeta,
    localMeta: DbMeta,
    rawDexie: Dexie,
  ): Promise<Db> {
    // Check if any migration in the range has holdWrites: true
    const holdWrites = migrationRangeHasHoldWrites(
      localMeta.schemaVersion,
      expected.schemaVersion,
      config.migrationRegistry,
    );

    if (holdWrites) {
      // Phase 8: block writes until migration + full re-sync completes. The DB
      // object is still valid for reads, but getDb() returns null because status
      // is 'recovering'. open() will not flip status to 'ready' for this case.
      setStatus('recovering');
    }
    // else: writes can continue immediately (default for Phase 1)

    // Write new _db_meta
    const now = new Date().toISOString();
    const newMeta: DbMeta = {
      ...localMeta,
      schemaVersion: expected.schemaVersion,
      appVersion: expected.appVersion,
      updatedAt: now,
    };
    await writeDbMeta(rawDexie, newMeta);
    config.telemetry?.trackEvent('local_db_incremental_migration_complete', {
      dbId: config.dbId,
      fromVersion: String(localMeta.schemaVersion),
      toVersion: String(expected.schemaVersion),
      holdWrites: String(holdWrites),
    });

    return db as Db;
  }

  /**
   * Execute update-app-first recovery.
   * Set status='updating-app' and trigger service worker update + reload.
   */
  async function executeUpdateAppFirst(): Promise<Db> {
    config.telemetry?.trackEvent('local_db_update_app_first', { dbId: config.dbId });
    setStatus('updating-app');
    db = null;

    if (config.onUpdateAppFirst) {
      try {
        await config.onUpdateAppFirst();
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        config.telemetry?.trackException(error, {
          dbId: config.dbId,
          operation: 'onUpdateAppFirst',
        });
        setStatus('error');
        throw new UpdateAppFirstError(`Failed to trigger app update: ${error.message}`);
      }
    }

    // If the callback didn't reload the page (e.g. offline, or no callback
    // configured), throw so the caller knows the DB is not available. This
    // case IS retryable — the caller should retry when online or when
    // auth/metadata changes.
    throw new UpdateAppFirstError('App update required — page reload expected. If offline, retry when online.', true);
  }

  // -------------------------------------------------------------------------
  // Main open() entry point
  // -------------------------------------------------------------------------

  async function open(
    expectedMetadata: ExpectedDbMeta | null,
    isOnline: boolean,
  ): Promise<Db> {
    // If already ready, return the existing DB
    if (status === 'ready' && db) {
      return db;
    }

    setStatus('initializing');

    try {
      // Open the Dexie database (schema upgrades run automatically if version is older)
      const openedDb = await config.openDb();
      db = openedDb;

      const rawDexie = config.getRawDexie();
      if (!rawDexie) {
        throw new Error('Failed to get raw Dexie instance after open');
      }

      // Read _db_meta
      const localMeta = await readDbMeta(rawDexie);

      // If no expected metadata (API doesn't support it yet or offline without compiled fallback),
      // use compiled-in constants as the expected metadata
      const expected: ExpectedDbMeta = expectedMetadata ?? {
        dbId: config.dbId,
        environment: 'dev', // safe default — will be corrected when API is available
        schemaVersion: config.expectedSchemaVersion,
        appVersion: '', // unknown — app version check will be skipped
        apiBaseUrl: '', // unknown — apiBaseUrl check will be skipped
      };

      // If expectedMetadata was null, skip environment/apiBaseUrl/appVersion checks
      // by aligning them with local values (so they don't trigger false wipes)
      if (!expectedMetadata && localMeta) {
        expected.environment = localMeta.environment;
        expected.apiBaseUrl = localMeta.apiBaseUrl;
        expected.appVersion = localMeta.appVersion;
      }

      // Check if DB has data (for the no-_db_meta decision)
      const hasData = localMeta ? true : await dbHasData(rawDexie);

      // Determine recovery action using compiled-in constants (local decision).
      // Phase 2 will add the API decision endpoint as the primary path when online.
      const decision = determineRecoveryAction(
        localMeta,
        expected,
        config.expectedSchemaVersion,
        config.minMigratableVersion,
        hasData,
      );

      config.telemetry?.trackEvent('local_db_recovery_decision', {
        dbId: config.dbId,
        action: decision.action,
        reason: decision.reason,
        isOnline: String(isOnline),
      });

      // Execute recovery based on the decision
      switch (decision.action) {
        case 'none': {
          // Write _db_meta if it doesn't exist (fresh DB)
          if (!localMeta) {
            const now = new Date().toISOString();
            await writeDbMeta(rawDexie, buildDbMeta(expected, now));
            config.telemetry?.trackEvent('local_db_meta_written', {
              dbId: expected.dbId,
              environment: expected.environment,
              schemaVersion: String(expected.schemaVersion),
              trigger: 'fresh_database',
            });
          } else if (compareSemver(localMeta.appVersion, expected.appVersion) < 0) {
            // Schema already matches; persist the newer expected appVersion so
            // the next open does not re-evaluate a stale local stamp.
            const now = new Date().toISOString();
            await writeDbMeta(rawDexie, {
              ...localMeta,
              appVersion: expected.appVersion,
              updatedAt: now,
            });
            config.telemetry?.trackEvent('local_db_app_version_stamped', {
              dbId: expected.dbId,
              fromVersion: localMeta.appVersion,
              toVersion: expected.appVersion,
            });
          }
          setStatus('ready');
          return openedDb;
        }

        case 'backfill-metadata': {
          // Pre-manager database with existing data — adopt it in place.
          // Write `_db_meta` from the expected metadata WITHOUT deleting
          // IndexedDB or clearing sync checkpoints. All existing local data
          // (including any unsynced offline writes) is preserved.
          //
          // If `expectedMetadata` is null (no server-authoritative metadata —
          // e.g. mobile opened fully offline before web-config ever loaded),
          // `expected` above is only a guessed fallback (environment: 'dev',
          // apiBaseUrl: ''). Writing that guess as this data's permanent
          // identity would falsely stamp real prod/stage data as 'dev', and
          // the very next online launch with real metadata would then see an
          // environment/apiBaseUrl mismatch and wipe the data the backfill
          // was meant to save. Defer the actual metadata write until a real
          // ExpectedDbMeta is available — the data is still fully usable
          // (status 'ready') for the rest of THIS session.
          //
          // NOTE: `open()` is only called once per session (useLocalDatabase's
          // `initDone` guard prevents re-invoking it even if real metadata
          // arrives later in the same session), so the deferred write does
          // NOT retry in-session when web-config eventually loads. It
          // completes on the next app launch/reload, which calls open() fresh
          // and re-evaluates this same decision — at that point, if online,
          // real metadata will be available and the backfill completes.
          if (!expectedMetadata) {
            config.telemetry?.trackEvent('local_db_metadata_backfill_deferred', {
              dbId: config.dbId,
              reason: 'no_expected_metadata_offline',
            });
            setStatus('ready');
            return openedDb;
          }

          const now = new Date().toISOString();
          await writeDbMeta(rawDexie, buildDbMeta(expected, now));
          config.telemetry?.trackEvent('local_db_metadata_backfilled', {
            dbId: expected.dbId,
            environment: expected.environment,
            schemaVersion: String(expected.schemaVersion),
          });
          setStatus('ready');
          return openedDb;
        }

        case 'incremental-migration': {
          const result = await executeIncrementalMigration(expected, localMeta!, rawDexie);
          // If holdWrites is active (Phase 8), executeIncrementalMigration leaves
          // status as 'recovering' and open() must not mark it 'ready' yet.
          if (status !== 'recovering') {
            setStatus('ready');
          }
          return result;
        }

        case 'wipe-and-rebuild': {
          const result = await executeWipeAndRebuild(expected);
          setStatus('ready');
          return result;
        }

        case 'update-app-first': {
          // This throws (page reload expected) or sets status='error'
          return await executeUpdateAppFirst();
        }

        default: {
          throw new Error(`Unknown recovery action: ${decision.action}`);
        }
      }
    } catch (err) {
      // update-app-first is not an error — it's a "page reload expected" state.
      // The status was already set to 'updating-app' by executeUpdateAppFirst().
      // Don't override it with 'error'.
      if (status !== 'updating-app') {
        setStatus('error');
        try {
          await config.closeDb();
        } catch (closeErr) {
          console.warn('[local-db-manager] Error closing database on open failure:', closeErr);
        }
        db = null;
      }
      const error = err instanceof Error ? err : new Error(String(err));
      config.telemetry?.trackException(error, {
        dbId: config.dbId,
        operation: 'localDatabaseManager.open',
        status,
      });
      throw err;
    }
  }

  async function close(): Promise<void> {
    try {
      await config.closeDb();
    } finally {
      db = null;
      setStatus('initializing');
    }
  }

  return {
    open,
    getStatus: () => status,
    subscribeStatus,
    getCanWrite,
    getDb,
    close,
  };
}

// ---------------------------------------------------------------------------
// Status → canWrite helper (exported for the hook and components)
// ---------------------------------------------------------------------------

/**
 * Returns true if writes are allowed for the given status.
 * Writes are allowed only during 'syncing-pending-writes' (mobile field work
 * continues while syncing pending writes) and 'ready' (normal operation).
 */
export function canWriteForStatus(status: DbStatus): boolean {
  return status === 'syncing-pending-writes' || status === 'ready';
}

/**
 * Returns true if the DB should be exposed (non-null) for the given status.
 * The DB is available in the same statuses where writes are allowed:
 * 'syncing-pending-writes' and 'ready'.
 *
 * @reusable dbAvailableForStatus
 * Usage: Delegates to `canWriteForStatus` to keep the two policies in sync.
 */
export function dbAvailableForStatus(status: DbStatus): boolean {
  return canWriteForStatus(status);
}
